import { useMutation, useQuery } from "@tanstack/react-query";
import {
  TutorAvailabilityParams,
  TutorAvailabilityItem,
  UserOneToOneCredits,
  DefaultErrorResponse,
  TutorOneToOneAvailabilityItem,
  UserOneToOneTopicsDone,
  NewTutorAvailabilityItem,
} from "@/types";
import API_ENDPOINTS from "@/data/client/endpoints.ts";
import client from "@/data/client";
import { enqueueSnackbar } from "notistack";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import routes from "@/config/routes.ts";

/**
 * @deprecated use useTutorAvailabilities instead
 * @param params
 * @param disable
 */
export function useTutorAvailabilitiesOld(
  params: Partial<TutorAvailabilityParams>,
  disable?: boolean,
) {
  const { data, isLoading, error } = useQuery<TutorAvailabilityItem, Error>(
    [API_ENDPOINTS.AVAILABILITIES_TUTOR, params],
    ({ queryKey }) =>
      client.oneToOne.tutorAvailabilities(Object.assign({}, queryKey[1])),
    {
      enabled: !disable,
    },
  );
  return {
    availabilities: data,
    isLoading,
    error,
  };
}

export function useTutorOneToOneAvailabilities(
  params: Partial<TutorAvailabilityParams>,
  disable?: boolean,
) {
  const { data, isLoading, error } = useQuery<
    TutorOneToOneAvailabilityItem,
    Error
  >(
    [API_ENDPOINTS.AVAILABILITIES_TUTOR_ONE_TO_ONE, params],
    ({ queryKey }) =>
      client.oneToOne.tutorOneToOneAvailabilities(
        Object.assign({}, queryKey[1]),
      ),
    {
      enabled: !disable,
    },
  );
  return {
    availabilities: data,
    isLoading,
    error,
  };
}

export function useTutorAvailabilities(
  params: Partial<TutorAvailabilityParams>,
  disable?: boolean,
) {
  const { data, isLoading, error } = useQuery<NewTutorAvailabilityItem, Error>(
    [API_ENDPOINTS.AVAILABILITIES_TUTOR_ALL, params],
    ({ queryKey }) =>
      client.oneToOne.newTutorOneToOneAvailabilities(
        Object.assign({}, queryKey[1]),
      ),
    {
      enabled: !disable,
    },
  );
  return {
    availabilities: data,
    isLoading,
    error,
  };
}

export function useOneToOneTopics() {
  const { data, isLoading, error } = useQuery<UserOneToOneTopicsDone, Error>(
    [API_ENDPOINTS.ONE_TO_ONE_TOPICS_DONE],
    client.oneToOne.getTopics,
  );
  return {
    oneToOneTopicsDone: data,
    isLoading,
    error,
  };
}

export function useOneToOneCredits() {
  const { data, isLoading, error } = useQuery<UserOneToOneCredits, Error>(
    [API_ENDPOINTS.ONE_TO_ONE_CREDITS],
    client.oneToOne.userCredits,
  );
  return {
    credits: data,
    isLoading,
    error,
  };
}

export function useBookOneToOneMutation(callback?: () => void) {
  const navigate = useNavigate();

  return useMutation(client.oneToOne.book, {
    onSuccess: (data) => {
      if (data.item) {
        enqueueSnackbar("Booking successful", {
          variant: "success",
        });
        if (callback) {
          callback();
        }
        navigate(routes.agenda, {
          replace: true,
        });
      } else {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}
